import React, { useState, useEffect } from "react";
import {
  getGroupsList,
  deleteGroup,
  activateGroup,
  updateOrderGroup,
  clearMembersGroup,
} from "../../../services/groupServices";
import { getDojos } from "../../../services/dojoServices";
import { getSettings, updateQuotaSettings } from "../../../services/settingsServices";
import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import ButtonRecycle from "../../../components/Button/ButtonRecycle";
import { FaPlus } from "react-icons/fa";
import { TbTriangleFilled, TbTriangleInvertedFilled } from "react-icons/tb";
import { ToastContainer, toast } from "react-toastify";
import CreateGroup from "./CreateGroup";
import EditGroupModal from "./EditGroupModal";

const SettingsGroups = () => {
  const [groups, setGroups] = useState([]);
  const [dojos, setDojos] = useState([]);
  const [selectedDojo, setSelectedDojo] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isQuotaActive, setIsQuotaActive] = useState(false);

  // Normalisation bool
  const toBool = (v) => v === true || v === 1 || v === "1" || v === "true";

  useEffect(() => {
    fetchDojos();
    fetchGroups();
    fetchQuotaSetting();
  }, []);

  const fetchDojos = async () => {
    try {
      const data = await getDojos();
      setDojos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des dojos", error);
      toast.error("Impossible de charger les dojos.");
    }
  };

  const fetchGroups = async () => {
    try {
      const data = await getGroupsList();
      setGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des groupes", error);
      toast.error("Impossible de charger les groupes.");
    }
  };

  const fetchQuotaSetting = async () => {
    try {
      const settings = await getSettings();
      const s = Array.isArray(settings) ? settings[0] : settings;
      setIsQuotaActive(toBool(s?.isquotaactive));
    } catch (error) {
      console.error("Erreur lors de la récupération du paramètre quota", error);
    }
  };

  const handleToggleQuota = async () => {
    const next = !isQuotaActive;
    const ok = window.confirm(
      next
        ? "Activer le blocage automatique des inscriptions lorsque le quota d'un groupe est atteint ?"
        : "Désactiver le blocage automatique des inscriptions lorsque le quota d'un groupe est atteint ?"
    );
    if (!ok) return;

    try {
      const res = await updateQuotaSettings(next);
      const value = toBool(res?.isquotaactive ?? next);
      setIsQuotaActive(value);
      toast.success(
        value
          ? "Quota activé : les inscriptions seront bloquées au quota."
          : "Quota désactivé : les inscriptions resteront possibles au-delà du quota."
      );
    } catch (error) {
      console.error("Erreur toggle quota", error);
      toast.error("Impossible de modifier le paramètre quota.");
    }
  };

  const handleDeleteGroup = async (group) => {
    if (group.nombreAdherents > 0) {
      toast.error("Impossible de supprimer un groupe avec des membres actifs.");
      return;
    }
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le groupe ${group.nom} ?`)) {
      try {
        await deleteGroup(group.id);
        toast.success("Groupe supprimé avec succès");
        fetchGroups();
      } catch (error) {
        toast.error("Erreur lors de la suppression d’un groupe");
        console.error(error);
      }
    }
  };

  const handleClearGroupMembers = async (group) => {
    if (group.nombreAdherents === 0) {
      toast.info("Ce groupe est déjà vide.");
      return;
    }
    const confirm = window.confirm(
      `Ce groupe contient ${group.nombreAdherents} adhérent(s).\nSouhaitez-vous vraiment le vider ?`
    );
    if (!confirm) return;

    try {
      await clearMembersGroup(group.id);
      toast.success("Groupe vidé avec succès.");
      fetchGroups();
    } catch (error) {
      toast.error("Erreur lors du vidage du groupe.");
      console.error(error);
    }
  };

  const toggleActiveStatus = async (groupId) => {
    try {
      await activateGroup(groupId);
      toast.success("Statut du groupe mis à jour avec succès");
      fetchGroups();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut du groupe");
      console.error(error);
    }
  };

  const openEditModal = (group) => setSelectedGroup(group);
  const closeEditModal = () => setSelectedGroup(null);

  const moveGroupUp = async (index) => {
    if (index === 0) return;
    const newGroups = [...groups];
    const temp = newGroups[index - 1];
    newGroups[index - 1] = newGroups[index];
    newGroups[index] = temp;
    newGroups[index - 1].ordre_groupe--;
    newGroups[index].ordre_groupe++;
    setGroups(newGroups);
    await updateOrderGroup(newGroups);
    fetchGroups();
  };

  const moveGroupDown = async (index) => {
    if (index === groups.length - 1) return;
    const newGroups = [...groups];
    const temp = newGroups[index + 1];
    newGroups[index + 1] = newGroups[index];
    newGroups[index] = temp;
    newGroups[index + 1].ordre_groupe++;
    newGroups[index].ordre_groupe--;
    setGroups(newGroups);
    await updateOrderGroup(newGroups);
    fetchGroups();
  };

  // Filtrage par Dojo
  const filteredGroups = selectedDojo
    ? groups.filter((g) => g.dojo_id === parseInt(selectedDojo))
    : groups;

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-1 sm:px-1">
        {/* CARD CONTAINER */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* HEADER dégradé */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                Gestion des Groupes
              </h2>
              <p className="text-white/90 text-sm sm:text-[15px] mt-1">
                Créez, filtrez et organisez vos groupes par dojo.
              </p>
            </div>
            <Button
              text="Créer un Groupe"
              icon={FaPlus}
              onClick={() => setIsCreateModalOpen(true)}
              className="!bg-white !text-slate-900 !hover:bg-white/90 !shadow-md !rounded-xl gap-2"
              hoverLabel="Créer un nouveau groupe"
            />
          </div>

          {/* CONTENT */}
          <div className="p-4 sm:p-6 space-y-4">
            {/* Filtre + Quota */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {/* Filtre Dojo */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white/60 dark:bg-slate-900/40">
                <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
                  Filtrer par dojo
                </label>
                <select
                  value={selectedDojo}
                  onChange={(e) => setSelectedDojo(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700
                             bg-white/80 text-slate-900 dark:bg-slate-900/50 dark:text-slate-100
                             focus:outline-none focus:ring-2 focus:ring-violet-400"
                >
                  <option value="">Tous les dojos</option>
                  {dojos.map((dojo) => (
                    <option key={dojo.id} value={dojo.id}>
                      {dojo.dojo_nom}
                    </option>
                  ))}
                </select>
              </div>

              {/* Quota Switch */}
              <div className="lg:col-span-2 rounded-xl border border-slate-200 dark:border-slate-800 p-4 bg-white/60 dark:bg-slate-900/40 flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-800 dark:text-slate-100">
                    Blocage à quota
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {isQuotaActive
                      ? "Activé — l'inscription est bloquée si le groupe est plein."
                      : "Désactivé — l'inscription reste possible même si le quota est atteint."}
                  </p>
                </div>
                <Button
                  text={isQuotaActive ? "Désactiver" : "Activer"}
                  onClick={handleToggleQuota}
                  className={`!rounded-xl ${isQuotaActive ? "!bg-red-500 hover:!bg-red-600" : "!bg-violet-500 hover:!bg-violet-600"} !text-white`}
                />
              </div>
            </div>

            {/* TABLE DESKTOP */}
            <div className="hidden md:block">
              <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40">
                <table className="min-w-full [&_thead_th]:text-slate-700 dark:[&_thead_th]:text-white">
                  <thead className="sticky top-0 z-10 bg-slate-50/90 dark:bg-slate-900/90 backdrop-blur">
                    <tr className="uppercase tracking-wide text-xs">
                      {["Nom", "Dojo", "Ordre", "Actif", "Nb Adh.", "Quota", "Enseignants", "Actions"].map(
                        (h) => (
                          <th key={h} className="px-4 py-3 text-left font-semibold">
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filteredGroups.map((group, index) => (
                      <tr
                        key={group.id}
                        className="group transition-all duration-150 hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                      >
                        {/* Nom + pastille couleur */}
                        <td className="px-4 py-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
                          <span className="inline-flex items-center gap-2">
                            <span className={`h-2.5 w-2.5 rounded-full ${group.color_groupe}`} />
                            {group.nom}
                          </span>
                        </td>

                        {/* Dojo */}
                        <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                          {dojos.find((d) => d.id === group.dojo_id)?.dojo_nom || "N/A"}
                        </td>

                        {/* Ordre + flèches */}
                        <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                          <div className="inline-flex items-center gap-2">
                            <span className="inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 px-2 text-sm font-semibold text-slate-800 dark:text-slate-100 bg-white/70 dark:bg-slate-900/40">
                              {group.ordre_groupe}
                            </span>
                            <div className="flex flex-col -my-1">
                              <button
                                onClick={() => moveGroupUp(index)}
                                className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                                title="Monter"
                              >
                                <TbTriangleFilled />
                              </button>
                              <button
                                onClick={() => moveGroupDown(index)}
                                className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                                title="Descendre"
                              >
                                <TbTriangleInvertedFilled />
                              </button>
                            </div>
                          </div>
                        </td>

                        {/* Actif */}
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => toggleActiveStatus(group.id)}
                            className={`px-2 py-1 rounded-md text-xs font-semibold transition ${
                              group.is_active
                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                            }`}
                            title="Basculer actif/inactif"
                          >
                            {group.is_active ? "Actif" : "Inactif"}
                          </button>
                        </td>

                        {/* Nb Adh. */}
                        <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                          {group.nombreAdherents}
                        </td>

                        {/* Quota */}
                        <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                          {group.quota_groupe}
                        </td>

                        {/* Enseignants */}
                        <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">
                          <div className="flex flex-col">
                            {[group.enseignant1, group.enseignant2, group.enseignant3, group.enseignant4, group.enseignant5, group.enseignant6]
                              .filter(Boolean)
                              .map((e, i) => (
                                <span key={i}>{e}</span>
                              ))}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <ButtonEdit onClick={() => openEditModal(group)} />
                            <ButtonRecycle onClick={() => handleClearGroupMembers(group)} />
                            <ButtonDelete onClick={() => handleDeleteGroup(group)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* CARDS MOBILE */}
            <div className="md:hidden space-y-3">
              {filteredGroups.map((group, index) => (
                <div
                  key={`card-${group.id}`}
                  className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 shadow-sm transition hover:shadow-md"
                >
                  <div className="flex items-start gap-4">
                    <div className={`shrink-0 h-10 w-10 rounded-lg ring-1 ring-slate-200 dark:ring-slate-700 ${group.color_groupe}`} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                          {group.nom}
                        </h3>
                        <div className="flex items-center gap-1">
                          <span className="inline-flex h-7 min-w-[2rem] items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 px-2 text-sm font-semibold text-slate-800 dark:text-slate-100 bg-white/70 dark:bg-slate-900/40">
                            {group.ordre_groupe}
                          </span>
                          <div className="flex flex-col -my-1">
                            <button
                              onClick={() => moveGroupUp(index)}
                              className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                              title="Monter"
                            >
                              <TbTriangleFilled />
                            </button>
                            <button
                              onClick={() => moveGroupDown(index)}
                              className="p-1 text-slate-500 hover:text-slate-800 dark:hover:text-white transition"
                              title="Descendre"
                            >
                              <TbTriangleInvertedFilled />
                            </button>
                          </div>
                        </div>
                      </div>

                      <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                        {dojos.find((d) => d.id === group.dojo_id)?.dojo_nom || "N/A"}
                      </p>

                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                        <button
                          onClick={() => toggleActiveStatus(group.id)}
                          className={`px-2 py-1 rounded-md font-semibold transition ${
                            group.is_active
                              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                              : "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                          }`}
                        >
                          {group.is_active ? "Actif" : "Inactif"}
                        </button>
                        <span className="px-2 py-1 rounded-md border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40">
                          {group.nombreAdherents} adh.
                        </span>
                        <span className="px-2 py-1 rounded-md border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40">
                          Quota: {group.quota_groupe}
                        </span>
                      </div>

                      <div className="mt-2 text-xs text-slate-600 dark:text-slate-300">
                        {[group.enseignant1, group.enseignant2, group.enseignant3, group.enseignant4, group.enseignant5, group.enseignant6]
                          .filter(Boolean)
                          .map((e, i) => (
                            <div key={i}>{e}</div>
                          ))}
                      </div>

                      <div className="mt-4 flex items-center gap-2">
                        <ButtonEdit onClick={() => openEditModal(group)} />
                        <ButtonRecycle onClick={() => handleClearGroupMembers(group)} />
                        <ButtonDelete onClick={() => handleDeleteGroup(group)} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* /CONTENT */}
        </div>
      </div>

      {/* Modales */}
      {isCreateModalOpen && (
        <CreateGroup
          isActive={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          refreshGroups={fetchGroups}
        />
      )}
      {selectedGroup && (
        <EditGroupModal
          isActive={Boolean(selectedGroup)}
          onClose={closeEditModal}
          refreshGroups={fetchGroups}
          groupData={selectedGroup}
        />
      )}

      <ToastContainer />
    </section>
  );
};

export default SettingsGroups;
